import React, { Suspense } from 'react';
import { isModuleRegistryTypeName, moduleRegistry, type ModuleRegistryTypeName } from './moduleRegistry';
import type { EnrichedModule, EnrichedModuleByTypeName } from './propInjection';
import { getModuleSchema } from 'utils/modules';
import Schema from 'components/shared/Schema/Schema';
import { ModuleErrorBoundary } from 'components/shared/ModuleErrorBoundary/ModuleErrorBoundary';

interface UnknownModule {
  __typename: string;
  schema?: {
    jsonLd?: object;
  };
}

interface ModuleRendererProps {
  module: EnrichedModule | UnknownModule;
  /** Pre-rendered children for container modules (e.g. PageSection) */
  children?: React.ReactNode;
}

function isRegisteredModule(module: ModuleRendererProps['module']): module is EnrichedModule {
  return isModuleRegistryTypeName(module.__typename);
}

function renderComponent<T extends ModuleRegistryTypeName>(
  typeName: T,
  module: EnrichedModuleByTypeName<T>,
  children?: React.ReactNode,
) {
  const Component = moduleRegistry[typeName];
  // @ts-expect-error — TS cannot correlate generic registry lookup with JSX spread props
  return <Component {...module}>{children}</Component>;
}

const ModuleContent = ({ module, children }: { module: EnrichedModule; children?: React.ReactNode }) => {
  const renderedModule = renderComponent(module.__typename, module, children);

  const schema = getModuleSchema(module);
  if (schema) {
    return (
      <>
        <Schema {...schema} />
        {renderedModule}
      </>
    );
  }

  return renderedModule;
};

/**
 * Generic recursive renderer that uses moduleRegistry for all lookups.
 * Zero experiment imports — rendering is fully decoupled from resolution.
 * Children are passed to every component; container components (e.g.
 * PageSectionShell) consume them, others ignore them.
 */
export const ModuleRenderer = ({ module, children }: ModuleRendererProps) => {
  if (!isRegisteredModule(module)) {
    return null;
  }

  // The Suspense boundary is load-bearing, not cosmetic. ModuleErrorBoundary is a client
  // component, but each module renders as a Server Component beneath it. A throw during the
  // server render (e.g. a stale-shape payload served from cache across a deploy) is only
  // recoverable by a client error boundary when it happens inside a Suspense boundary; with
  // no Suspense, React fails the whole shell and the page 500s instead of the boundary
  // dropping the single bad module. Suspense turns the server-render throw into a per-module
  // recoverable error the boundary can contain.
  return (
    <ModuleErrorBoundary moduleType={module.__typename} moduleId={module.sys?.id}>
      <Suspense fallback={null}>
        <ModuleContent module={module}>{children}</ModuleContent>
      </Suspense>
    </ModuleErrorBoundary>
  );
};
